/**
 * Adapts API coverage into the shape the rich Media views render.
 *
 * The rich coverage views (social feed, traditional table, the overview) render
 * a presentation shape — `CoverageItem` — that predates the API. Rather than
 * rewrite eight hundred lines of well-judged rendering to speak snake_case, this
 * adapts the server's documents into that shape once, in one place.
 *
 * Coverage lives in Mongo and the documents are genuinely ragged: an RSS
 * publication carries an author and no metrics, an owned post carries metrics
 * and an account id, a vendor webhook carries neither. Every optional field is
 * treated as optional here rather than assumed from whichever ingest path was
 * looked at last.
 *
 * What the adapter deliberately does NOT invent:
 *   - `metrics` defaults to an empty object, not to zeros. A zero reads as
 *     "nobody saw this"; absence reads as "we don't know", and those are
 *     different facts. (This repo has shipped that bug before, in search, where
 *     Elasticsearch errors became `0` counts.)
 *   - `sentiment` passes `unknown` straight through. It means the classifier has
 *     not run, and folding it into neutral would turn the sentiment mix into a
 *     chart of outstanding work.
 */
import { useMemo } from 'react';
import type {
  CoverageItem,
  MeasurementBasis,
  MediaChannel,
  MediaClass,
  MediaIngestSource,
  MediaItemStatus,
  MediaKind,
  MediaMetrics,
  MediaOutlet,
  MediaSentiment,
  MediaSubject,
  SocialPlatform,
  SourceAccount,
  SourceTier,
  TrackedKeyword,
  TraditionalMedium,
} from '@/client/types/media';
import type {
  ApiCoverageItem,
  ApiMediaOutlet,
  ApiSourceAccount,
  ApiTrackedKeyword,
} from '@/client/types/marketing/api';
import { useCoverageQuery } from '@/client/api/marketing/radar';
import {
  useMediaOutletsQuery,
  useSourceAccountsQuery,
  useTrackedKeywordsQuery,
} from '@/client/api/marketing/sources';

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? (value as unknown[]).map(String) : [];

/** The API stores these as free strings; fall back rather than render blank. */
const asChannel = (v: string | null | undefined): MediaChannel =>
  v === 'social' ? 'social' : 'traditional';

const asKind = (v: string | null | undefined): MediaKind =>
  (['post', 'mention', 'publication', 'clip'] as const).includes(v as MediaKind)
    ? (v as MediaKind)
    : 'publication';

const asClass = (v: string | null | undefined): MediaClass =>
  (['owned', 'earned', 'shared', 'paid'] as const).includes(v as MediaClass)
    ? (v as MediaClass)
    : 'earned';

const asSentiment = (v: string | null | undefined): MediaSentiment =>
  (['positive', 'neutral', 'negative', 'unknown'] as const).includes(v as MediaSentiment)
    ? (v as MediaSentiment)
    : 'unknown';

const asSubject = (v: string | null | undefined): MediaSubject =>
  (['brand', 'competitor', 'topic'] as const).includes(v as MediaSubject)
    ? (v as MediaSubject)
    : 'brand';

const asIngest = (v: string | null | undefined): MediaIngestSource =>
  (['manual', 'url', 'rss', 'webhook', 'vendor', 'discover'] as const).includes(
    v as MediaIngestSource,
  )
    ? (v as MediaIngestSource)
    : 'manual';

const asBasis = (v: string | null | undefined): MeasurementBasis | undefined =>
  v === 'public' || v === 'connected' ? v : undefined;

/**
 * Map API metric bags into the presentation shape. Accepts camelCase or
 * snake_case keys; omits absent figures so UIs can distinguish "not reported"
 * from zero.
 */
function adaptMetrics(raw: Record<string, number> | null | undefined): MediaMetrics {
  if (!raw || typeof raw !== 'object') return {};
  const num = (k: string, alt?: string): number | undefined => {
    const v = raw[k] ?? (alt ? raw[alt] : undefined);
    return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
  };
  const out: MediaMetrics = {};
  const reach = num('reach', 'estimated_reach');
  const likes = num('likes');
  const comments = num('comments');
  const shares = num('shares');
  const views = num('views');
  const ave = num('ave');
  const prValue = num('prValue', 'pr_value');
  const impressions = num('impressions');
  const saves = num('saves');
  const replies = num('replies');
  const engagementRate = num('engagementRate', 'engagement_rate');
  if (reach != null) out.reach = reach;
  if (likes != null) out.likes = likes;
  if (comments != null) out.comments = comments;
  if (shares != null) out.shares = shares;
  if (views != null) out.views = views;
  if (ave != null) out.ave = ave;
  if (prValue != null) out.prValue = prValue;
  if (impressions != null) out.impressions = impressions;
  if (saves != null) out.saves = saves;
  if (replies != null) out.replies = replies;
  if (engagementRate != null) out.engagementRate = engagementRate;
  return out;
}

export function adaptCoverage(item: ApiCoverageItem): CoverageItem {
  const channel = asChannel(item.channel);
  const captured = item.captured_at ?? item.first_seen_at ?? '';
  const metricsPublic =
    item.metrics_public != null ? adaptMetrics(item.metrics_public) : undefined;
  return {
    id: item.id,
    channel,
    kind: asKind(item.kind),
    mediaClass: asClass(item.media_class),
    platform: (item.platform as SocialPlatform | undefined) ?? undefined,
    outletId: item.outlet_id != null ? String(item.outlet_id) : undefined,
    outletName: item.outlet_name ?? item.site_name ?? undefined,
    title: item.title,
    summary: item.summary ?? '',
    bodyExcerpt: item.body_excerpt ?? undefined,
    url: item.url ?? undefined,
    // Some documents carry only one of the two dates. Falling back keeps the
    // sort stable instead of dropping undated items to the bottom forever.
    publishedAt: item.published_at ?? captured,
    capturedAt: captured || (item.published_at ?? ''),
    author: item.author ?? undefined,
    language: item.language ?? 'en',
    sentiment: asSentiment(item.sentiment),
    status: (item.status as MediaItemStatus) ?? 'new',
    // Discover leaves urgency unset — null must survive so crisis tiles stay quiet.
    urgencyScore: item.urgency_score == null ? null : item.urgency_score,
    internalNote: item.internal_note ?? undefined,
    tags: strings(item.tags),
    topics: strings(item.topics),
    matchedRuleIds: strings(item.matched_rule_ids),
    // Entity links are a planned join, not a stored field. An empty list is the
    // honest answer; inventing one would put fake chips on every row.
    links: [],
    metrics: adaptMetrics(item.metrics),
    metricsPublic:
      metricsPublic && Object.keys(metricsPublic).length > 0 ? metricsPublic : undefined,
    measurementBasis: asBasis(item.measurement_basis),
    subject: asSubject(item.subject),
    sourceIngest: asIngest(item.source_ingest),
    social:
      channel === 'social'
        ? {
            // The document does not record a post format, and guessing one would
            // put a confident wrong label on every social row.
            format: 'text',
            accountId: item.said != null ? String(item.said) : undefined,
            authorHandle: item.author_handle ?? item.author ?? '',
            authorDisplayName: item.author ?? item.author_handle ?? '',
            caption: item.summary ?? undefined,
          }
        : undefined,
  };
}

export function adaptKeyword(k: ApiTrackedKeyword): TrackedKeyword {
  return {
    id: String(k.tkid),
    label: k.label,
    terms: strings(k.terms),
    matchMode: k.match_mode === 'all' ? 'all' : 'any',
    channels: strings(k.channels).filter(
      (c): c is MediaChannel => c === 'social' || c === 'traditional',
    ),
    enabled: !!k.enabled,
    createdAt: '',
    updatedAt: '',
  };
}

/**
 * Server-side published_at window for GET /radar/coverage.
 * Client-only filtering of a capped page silently under-reports long ranges.
 *
 * Timestamps are floored to the UTC day so React Query keys stay stable across
 * re-renders. Using `new Date().toISOString()` inline would change every ms and
 * refetch forever ("Updating…" stuck on).
 */
export function publishedRangeParams(
  rangeDays: number | 'all',
): { from?: string; to?: string } {
  if (rangeDays === 'all') return {};
  const end = new Date();
  end.setUTCHours(23, 59, 59, 999);
  const start = new Date(end.getTime() - rangeDays * 86_400_000);
  start.setUTCHours(0, 0, 0, 0);
  return { from: start.toISOString(), to: end.toISOString() };
}

/**
 * Coverage in the shape the rich views render.
 *
 * Returns the query alongside the items so callers can still distinguish
 * loading, unreachable and genuinely empty — an adapter that returned a bare
 * array would collapse all three into `[]`.
 *
 * Prefer passing `from` / `to` (via `publishedRangeParams`) whenever the UI
 * applies a time window — see listening integration guide §8.0.
 */
export function useCoverageItems(params: Record<string, unknown> = {}) {
  const query = useCoverageQuery({ limit: 500, ...params });
  const items = useMemo(
    () => (query.data?.items ?? []).map(adaptCoverage),
    [query.data],
  );
  return { ...query, items };
}

/**
 * Accounts and outlets in the presentation shape the views filter by.
 *
 * Ids become strings here only because `SourceAccount.id` and `MediaOutlet.id`
 * are strings in that shape — the numeric primary key is stringified, not
 * prefixed, so `String(said)` round-trips. New code should use `SourceRef` from
 * `types/marketing/sources` rather than either.
 */
export function adaptAccount(a: ApiSourceAccount): SourceAccount {
  return {
    id: String(a.said),
    platform: a.platform as SocialPlatform,
    handle: a.handle,
    displayName: a.display_name,
    owned: !!a.owned,
    useAsDestination: !!a.use_as_destination,
    useAsListen: !!a.use_as_listen,
    active: !!a.active,
    tier: (a.tier as SourceTier) ?? 2,
    followers: a.followers ?? undefined,
    verified: !!a.verified,
    region: a.region ?? undefined,
    topics: strings(a.topics),
    ownerName: a.owner_name ?? undefined,
    contactEmail: a.contact_email ?? undefined,
    profileUrl: a.profile_url ?? undefined,
    lastCoveredAt: a.last_covered_at ?? undefined,
    notes: a.notes ?? undefined,
    ownership: (a.ownership as SourceAccount['ownership']) ?? undefined,
  };
}

export function adaptOutlet(o: ApiMediaOutlet): MediaOutlet {
  return {
    id: String(o.moid),
    name: o.name,
    medium: o.medium as TraditionalMedium,
    tier: (o.tier as SourceTier) ?? 2,
    useAsDestination: !!o.use_as_destination,
    useAsListen: !!o.use_as_listen,
    active: !!o.active,
    region: o.region ?? undefined,
    website: o.website ?? undefined,
    beats: strings(o.beats),
    ownerName: o.owner_name ?? undefined,
    contactEmail: o.contact_email ?? undefined,
    pitchEmail: o.pitch_email ?? undefined,
    reach: o.reach ?? undefined,
    doNotPitch: !!o.do_not_pitch,
    notes: o.notes ?? undefined,
    ownership: (o.ownership as MediaOutlet['ownership']) ?? undefined,
  };
}

export function useAdaptedAccounts() {
  const query = useSourceAccountsQuery({ limit: 200 });
  return useMemo(() => (query.data?.items ?? []).map(adaptAccount), [query.data]);
}

export function useAdaptedOutlets() {
  const query = useMediaOutletsQuery({ limit: 200 });
  return useMemo(() => (query.data?.items ?? []).map(adaptOutlet), [query.data]);
}

export function useApiTrackedKeywords() {
  const query = useTrackedKeywordsQuery({ limit: 100 });
  const keywords = useMemo(
    () => (query.data?.items ?? []).map(adaptKeyword),
    [query.data],
  );
  return { ...query, keywords };
}
